import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  GraduationCap, 
  Heart, 
  BookOpen, 
  Calendar, 
  MapPin,
  Cake,
  Clock,
  PartyPopper,
  UserX
} from 'lucide-react';
import { Student } from '@/types/models';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface StudentCardProps {
  student: Student;
  familyName?: string;
  enrollmentCount: number;
  onClick: () => void;
}

const StudentCard: React.FC<StudentCardProps> = ({ 
  student, 
  familyName, 
  enrollmentCount, 
  onClick 
}) => {
  const getAge = () => {
    if (!student.date_of_birth) return null;
    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getAgeGroup = (age: number | null) => {
    if (!age) return { label: 'Unknown', color: 'text-gray-500', bg: 'bg-gray-50' };
    if (age <= 10) return { label: 'Young', color: 'text-green-600', bg: 'bg-green-50' };
    if (age <= 17) return { label: 'Teen', color: 'text-blue-600', bg: 'bg-blue-50' };
    return { label: 'Adult', color: 'text-purple-600', bg: 'bg-purple-50' };
  };

  const getEnrollmentStatus = () => {
    if (enrollmentCount === 0) {
      return {
        label: 'Not Enrolled',
        variant: 'outline' as const,
        color: 'text-orange-600',
        bg: 'bg-orange-50'
      };
    } else if (enrollmentCount === 1) {
      return {
        label: '1 Class',
        variant: 'default' as const,
        color: 'text-blue-600',
        bg: 'bg-blue-50'
      };
    } else {
      return {
        label: `${enrollmentCount} Classes`,
        variant: 'default' as const,
        color: 'text-green-600',
        bg: 'bg-green-50'
      };
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || 'S';
    const last = lastName?.charAt(0) || 'T';
    return `${first}${last}`.toUpperCase();
  };

  const isRecentBirthday = () => {
    if (!student.date_of_birth) return false;
    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    
    // Check if birthday is within the last 7 days or next 7 days
    const thisYearBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    const daysDiff = Math.abs((today.getTime() - thisYearBirthday.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysDiff <= 7;
  };

  // Helper to check if birthday is today or tomorrow
  const getBirthdayStatus = () => {
    if (!student.date_of_birth) return null;
    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    const thisYearBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    const diffDays = Math.floor((thisYearBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    return null;
  };

  const age = getAge();
  const ageGroup = getAgeGroup(age);
  const enrollmentStatus = getEnrollmentStatus();
  const recentBirthday = isRecentBirthday();
  const birthdayStatus = getBirthdayStatus();

  return (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      className="h-full"
    >
      <Card 
        className="h-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/15 hover:border-primary/40 hover:-translate-y-1 group cursor-pointer border border-gray-200"
        onClick={onClick}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-gradient-to-br from-purple-100 to-blue-100 text-purple-700">
                    {getInitials(student.first_name, student.last_name)}
                  </AvatarFallback>
                </Avatar>
                {birthdayStatus && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full border-2 border-white shadow-lg flex items-center justify-center
                          ${birthdayStatus === 'today' ? 'bg-gradient-to-r from-pink-400 to-red-500' : 'bg-gradient-to-r from-yellow-400 to-orange-500'}`}
                        >
                          {birthdayStatus === 'today' ? (
                            <Cake className="h-5 w-5 text-white" />
                          ) : (
                            <PartyPopper className="h-5 w-5 text-white" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {birthdayStatus === 'today' ? 'Birthday today!' : 'Birthday tomorrow!'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <div>
                <CardTitle className="text-lg font-semibold group-hover:text-primary transition-colors duration-200 leading-tight">
                  {student.first_name || 'Unknown'} {student.last_name || 'Student'}
                </CardTitle>
                {student.preferred_name && student.preferred_name !== student.first_name && (
                  <p className="text-sm text-muted-foreground">
                    "{student.preferred_name}"
                  </p>
                )}
              </div>
            </div>
            
            <Badge variant={enrollmentStatus.variant} className="shrink-0">
              <BookOpen className="h-3 w-3 mr-1" />
              {enrollmentStatus.label}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Family & Age Info */}
          <div className="space-y-3">
            {familyName ? (
              <div className="flex items-center gap-2 p-2 bg-pink-50 rounded-lg border border-pink-100">
                <Heart className="h-4 w-4 text-pink-500" />
                <span className="text-sm font-medium text-pink-700 truncate">{familyName} Family</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg border border-orange-100">
                <UserX className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-700">No family assigned</span>
              </div>
            )}
            
            {age !== null && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-blue-500" />
                <span className={ageGroup.color}>
                  {age} years old • {ageGroup.label}
                </span>
              </div>
            )}
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`${enrollmentStatus.bg} rounded-lg p-3 text-center transition-all duration-200 hover:scale-105 cursor-pointer`}>
              <div className="flex items-center justify-center gap-1 mb-1">
                <GraduationCap className="h-4 w-4 text-blue-500" />
              </div>
              <div className="text-lg font-semibold text-blue-700">{enrollmentCount}</div>
              <div className="text-xs text-blue-600">
                {enrollmentCount === 1 ? 'Enrollment' : 'Enrollments'}
              </div>
            </div>
            
            <div className={`${ageGroup.bg} rounded-lg p-3 text-center transition-all duration-200 hover:scale-105 cursor-pointer`}>
              <div className="flex items-center justify-center gap-1 mb-1">
                <Clock className="h-4 w-4 text-purple-500" />
              </div>
              <div className="text-lg font-semibold text-purple-700">
                {age !== null ? age : '--'}
              </div>
              <div className="text-xs text-purple-600">
                Years Old
              </div>
            </div>
          </div>

          {/* Location & Additional Info */}
          <div className="pt-2 border-t border-border/50">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>ID: {student.id.slice(-8)}</span>
              <span className="text-right">
                {student.created_at && new Date(student.created_at).toLocaleDateString()}
              </span>
            </div>
            
            {student.notes && (
              <div className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1 truncate">
                Note: {student.notes}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default StudentCard;